import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, FileText, Upload, Download, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';

const DOC_CATEGORIES = [
  { value: 'certificazione', label: 'Certificazione' },
  { value: 'polizza', label: 'Polizza' },
  { value: 'contratto', label: 'Contratto' },
  { value: 'procedura', label: 'Procedura' },
  { value: 'pos', label: 'POS' },
  { value: 'dvr', label: 'DVR' },
  { value: 'autorizzazione', label: 'Autorizzazione' },
  { value: 'durc', label: 'DURC' },
  { value: 'visura', label: 'Visura Camerale' },
  { value: 'sgi', label: 'SGI' },
  { value: 'altro', label: 'Altro' },
];

export default function CompanyDocsPage() {
  const { isRole } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [form, setForm] = useState({
    category: 'altro',
    title: '',
    document_code: '',
    revision: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });

  const load = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterCategory && filterCategory !== '__all__') params.category = filterCategory;
      const res = await api.get('/company-documents', { params });
      setDocs(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, filterCategory]);

  const handleCreate = async () => {
    try {
      await api.post('/company-documents', form);
      setShowCreate(false);
      setForm({ category: 'altro', title: '', document_code: '', revision: '', issue_date: '', expiry_date: '', notes: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUpload = async (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/upload/company-doc/${docId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/company-documents/${docId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      valido: 'bg-green-50 text-green-700 border-green-200',
      in_scadenza: 'bg-amber-50 text-amber-700 border-amber-200',
      scaduto: 'bg-red-50 text-red-700 border-red-200',
    };
    const labels = {
      valido: 'Valido',
      in_scadenza: 'In Scadenza',
      scaduto: 'Scaduto',
    };
    return <Badge variant="outline" className={styles[status] || styles.valido}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-docs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Documenti Aziendali</h1>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca documento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="company-docs-search"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40" data-testid="filter-category">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              {DOC_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isRole('admin', 'aspp') && (
            <Button onClick={() => setShowCreate(true)} data-testid="add-doc-btn">
              <Plus size={16} className="mr-1" /> Nuovo
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titolo</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead className="hidden sm:table-cell">Codice</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="hidden md:table-cell">File</TableHead>
                {isRole('admin', 'aspp') && <TableHead className="w-20">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Nessun documento trovato
                  </TableCell>
                </TableRow>
              ) : docs.map(doc => (
                <TableRow key={doc.id} className="hover:bg-slate-50" data-testid={`doc-row-${doc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-slate-400" />
                      <span className="font-medium text-sm">{doc.title}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                      {DOC_CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-slate-600">
                    {doc.document_code || '-'}
                  </TableCell>
                  <TableCell className="text-sm">{doc.expiry_date ? fmtDate(doc.expiry_date) : 'N/A'}</TableCell>
                  <TableCell>{statusBadge(doc.status)}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {doc.file_url ? (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                      >
                        <Download size={14} /> Scarica
                      </a>
                    ) : isRole('admin', 'aspp') ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowUpload(doc.id)}
                        className="text-xs"
                      >
                        <Upload size={14} className="mr-1" /> Carica
                      </Button>
                    ) : (
                      <span className="text-slate-400 text-xs">-</span>
                    )}
                  </TableCell>
                  {isRole('admin', 'aspp') && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Documento Aziendale</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Titolo *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="es. DURC Gennaio 2026"
                data-testid="doc-title"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Codice Documento</Label>
              <Input
                value={form.document_code}
                onChange={(e) => setForm({ ...form, document_code: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Data Emissione</Label>
              <Input
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Data Scadenza</Label>
              <Input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.title} data-testid="doc-save-btn">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={!!showUpload} onOpenChange={(o) => { if (!o) { setShowUpload(null); setPendingFile(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Carica File</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
            <FilePreview file={pendingFile} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowUpload(null); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={() => { handleUpload(showUpload, pendingFile); setPendingFile(null); }} disabled={!pendingFile} data-testid="upload-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
